import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Modal, Form, Alert, OverlayTrigger, Tooltip } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faHandHoldingUsd, faQuestionCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/api';
import Ranked from '../components/Ciem/Ranked';
import CardLayout from '../components/Layout/CardLayout';

const FinanciacionPage = () => {
    const [financiaciones, setFinanciaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [showTengaPresenteModal, setShowTengaPresenteModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedFinanciacion, setSelectedFinanciacion] = useState(null);
    const [formData, setFormData] = useState({
        banco: '',
        linea: '',
        plazo: '',
        interesNominal: '',
        interesEfectivoAnual: '',
        formaPago: ''
    });

    const ciemId = useSelector(state => state.ciem.ciemId);
    const [rankedForm, setRankedForm] = useState([]);

    // Fetch financing conditions on component mount
    useEffect(() => {
        if (ciemId) {
            fetchFinanciaciones();
        } else {
            setLoading(false);
        }

        axiosInstance.get('/detailoption/option/value/rankedTen').then((response) => {
            console.log("Calificación obtenida:", response.data.data[0]);
            setRankedForm(response.data.data[0]);
        }).catch((e) => {
            alert("Error consultando la calificacion " + e)
        });
    }, [ciemId]);

    const fetchFinanciaciones = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/condicionFinanciacion/${ciemId}`);
            // Handle different response structures - could be array directly or nested in data property
            const data = Array.isArray(response.data) ? response.data : (response.data.data || []);
            setFinanciaciones(data);
            setError(null);
        } catch (err) {
            setError('Error al cargar las condiciones de financiación: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleShowCreateModal = () => {
        setModalMode('create');
        setSelectedFinanciacion(null);
        setFormData({
            banco: '',
            linea: '',
            plazo: '',
            interesNominal: '',
            interesEfectivoAnual: '',
            formaPago: ''
        });
        setShowModal(true);
    };

    const handleShowEditModal = (financiacion) => {
        setModalMode('edit');
        setSelectedFinanciacion(financiacion);
        setFormData({
            banco: financiacion.banco || '',
            linea: financiacion.linea || '',
            plazo: financiacion.plazo || '',
            interesNominal: financiacion.interesNominal || '',
            interesEfectivoAnual: financiacion.interesEfectivoAnual || '',
            formaPago: financiacion.formaPago || ''
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedFinanciacion(null);
        setFormData({
            banco: '',
            linea: '',
            plazo: '',
            interesNominal: '',
            interesEfectivoAnual: '',
            formaPago: ''
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submitData = {
                ...formData,
                id: selectedFinanciacion ? selectedFinanciacion.id : null,
                ciemId: ciemId,
                interesNominal: formData.interesNominal ? parseFloat(formData.interesNominal) : null,
                interesEfectivoAnual: formData.interesEfectivoAnual ? parseFloat(formData.interesEfectivoAnual) : null
            };

            if (modalMode === 'create') {
                await axiosInstance.post('/condicionFinanciacion/', submitData);
            } else {
                await axiosInstance.patch('/condicionFinanciacion/', submitData);
            }
            handleCloseModal();
            fetchFinanciaciones();
        } catch (err) {
            setError(`Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} condición de financiación: ` + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de que desea eliminar esta condición de financiación?')) {
            try {
                await axiosInstance.delete(`/condicionFinanciacion/${id}`);
                fetchFinanciaciones();
            } catch (err) {
                setError('Error al eliminar condición de financiación: ' + err.message);
            }
        }
    };

    if (!ciemId) {
        return (
            <Container className="mt-4">
                <Alert variant="warning">
                    Por favor, seleccione un CIEM para ver las condiciones de financiación.
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="px-0">
            <Row>
                <Col xs={12} className="p-3">
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="d-flex align-items-center">
                                    <h3 className="mb-0 me-3">
                                        <FontAwesomeIcon icon={faHandHoldingUsd} className="me-2" />
                                        Financiación
                                    </h3>
                                    <Button
                                        variant="outline-info"
                                        size="sm"
                                        className="me-2"
                                        onClick={() => setShowHelpModal(true)}
                                        title="Ayuda"
                                    >
                                        <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                                        Ayuda
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => setShowTengaPresenteModal(true)}
                                        title="Tenga presente"
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                                        Tenga presente
                                    </Button>
                                </div>
                                <Button variant="primary" onClick={handleShowCreateModal}>
                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                    Crear
                                </Button>
                            </div>
                            <p className="mb-0 text-justify" style={{ textAlign: 'justify' }}>
                                Indique los bancos y otras entidades financieras de las que es posible obtener crédito.
                                En la tabla ponga en primer lugar aquella en la cual es más factible conseguir la financiación.
                            </p>
                        </Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                </div>
                            ) : (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Indique los bancos y otras entidades financieras de las que es posible obtener financiación. Ponga de primera aquella con la cual es más factible alcanzar este propósito.</Tooltip>}>
                                                    <span>Banco <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Los bancos ofrecen crédito con dos líneas principales: recursos propios y recursos provenientes del Estado. Señale si el crédito se puede tomar de una línea dotada con recursos del Estado, de fomento a la inversión empresarial, que intermedian los bancos, lo que normalmente es mejor para los proyectos emprendedores, pues las condiciones de financiación suelen ser más blandas, al concederse plazos de crédito más largos, periodos de gracia en los cuales no se paga la cuota del crédito y la tase de interés suele ser más baja.</Tooltip>}>
                                                    <span>Línea <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Indique el plazo en que se puede tomar el crédito. Si el crédito se tomará para financiar la compra de activos fijos como maquinaria y equipo de producción, los plazos para pagar suelen ser de tres a cinco o hasta siete años. Por su parte, los créditos destinados a financiar capital de trabajo, es decir, la compra de materias primas, pago de trabajadores, cubrimiento de costos fijos y concesión de crédito a clientes para estimular ventas, por lo regular son de corto plazo, a saber, de uno a tres años como máximo, pues se considera que el dinero aplicado a financiar estos rubros debe retornar a la empresa más rápidamente que el dinero aplicado a la compra de activos fijos.</Tooltip>}>
                                                    <span>Plazo <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>La tasa de interés nominal es la tasa de interés de referencia del mercado financiero, así, es la tasa a la que las entidades prestamistas anuncia conceden crédito. A partir de ella se obtiene la tasa efectiva, en función de las condiciones de capitalización, es decir, el número de veces o periodos en que se deben pagar los intereses durante el periodo de tiempo en que se pactó un crédito. Indique la tasa de interés nominal anual a la que es posible obtener los recursos que se requiera obtener por vía de préstamo.</Tooltip>}>
                                                    <span>Interés Nominal <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Indique la tasa de interés efectiva anual a la que es equivalente la tasa de interés nominal anual. Esta es la verdadera tasa de interés que se obtiene al prestar o invertir dinero o la que se paga por un crédito obtenido. Esta tasa la informan las entidades crediticias del sector financiero.</Tooltip>}>
                                                    <span>Interés Efectivo Anual <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>
                                                <OverlayTrigger placement="top" overlay={<Tooltip>Señale la forma de pago en que se debe cancelar el crédito. Esto puede ser mensual, trimestral o en otro periodo de tiempo. Indique si es posible obtener periodo de gracia, es decir, un tiempo durante el que solo se pagan los intereses del crédito a tomar y no se amortiza al capital de la deuda. Esto sirve para evitar que la empresa disminuya muy rápidamente el capital que necesita para operar.</Tooltip>}>
                                                    <span>Forma de Pago <FontAwesomeIcon icon={faQuestionCircle} size="xs" /></span>
                                                </OverlayTrigger>
                                            </th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {financiaciones.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    No hay condiciones de financiación registradas
                                                </td>
                                            </tr>
                                        ) : (
                                            financiaciones.map((financiacion) => (
                                                <tr key={financiacion.id}>
                                                    <td>{financiacion.banco || '-'}</td>
                                                    <td>{financiacion.linea || '-'}</td>
                                                    <td>{financiacion.plazo || '-'}</td>
                                                    <td>{financiacion.interesNominal !== null && financiacion.interesNominal !== undefined ? financiacion.interesNominal : '-'}</td>
                                                    <td>{financiacion.interesEfectivoAnual !== null && financiacion.interesEfectivoAnual !== undefined ? financiacion.interesEfectivoAnual : '-'}</td>
                                                    <td>{financiacion.formaPago || '-'}</td>
                                                    <td>
                                                        <Button
                                                            variant="info"
                                                            size="sm"
                                                            className="me-2"
                                                            onClick={() => handleShowEditModal(financiacion)}
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </Button>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(financiacion.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Crear Nueva Condición de Financiación' : 'Editar Condición de Financiación'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Banco</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="banco"
                                        value={formData.banco}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Línea</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="linea"
                                        value={formData.linea}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Plazo</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="plazo"
                                        value={formData.plazo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Forma de Pago</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="formaPago"
                                        value={formData.formaPago}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Interés Nominal</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="interesNominal"
                                        value={formData.interesNominal}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Interés Efectivo Anual</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        name="interesEfectivoAnual"
                                        value={formData.interesEfectivoAnual}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {modalMode === 'create' ? 'Crear' : 'Actualizar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Help Modal - RECUERDE */}
            <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>RECUERDE</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ textAlign: 'justify' }}>
                        Si se conoce que bancos y otras entidades financieras conceden créditos a empresas que compiten
                        en el negocio planteado, ello indica que es factible financiar la iniciativa emprendedora con
                        recursos planteados y aportes de socios.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHelpModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Tenga Presente Modal */}
            <Modal show={showTengaPresenteModal} onHide={() => setShowTengaPresenteModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Tenga presente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ textAlign: 'justify' }}>
                        <p>
                            Los bancos ofrecen crédito con dos líneas principales: recursos propios y recursos provenientes
                            del Estado. Señale si el crédito se puede tomar de una línea dotada con recursos del Estado de
                            fomento a la inversión empresarial que intermedian los bancos. Esto normalmente es mejor para
                            los proyectos emprendedores, pues las condiciones suelen ser más blandas, es decir, se pueden
                            obtener mayores plazos, menores tasas de interés y condonaciones de intereses y del valor de la deuda.
                        </p>
                        <p>
                            Señale el plazo en el que se puede tomar el crédito. Si el crédito se tomará para financiar la
                            compra de activos fijos como maquinaria y equipo de producción, los plazos para pagar suelen ser
                            de tres a cinco o hasta siete años. Por otra parte, los créditos destinados a financiar capital
                            de trabajo, es decir, la compra de materias primas, pago de trabajadores, cubrimiento de costos
                            fijos y concesión de crédito a clientes para estimular ventas, por lo regular son de corto plazo,
                            a saber, de uno a tres años como máximo, pues se considera que el dinero aplicado a financiar
                            estos rubros debe retornar a la empresa más rápidamente que el dinero aplicado a la compra de
                            activos fijos.
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTengaPresenteModal(false)}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Ranked - Calificación */}
            {(() => {
                const calificacion = <Ranked field={rankedForm} ciemId={ciemId} />;
                return <CardLayout value={"rankedTen"} tablaComponent={calificacion} />;
            })()}
        </Container>
    );
};

export default FinanciacionPage;
